import { useFormik } from 'formik';
import _debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

// Components
import RadioGroup from '../components/pageComponents/Custom/RadioGroup';
import FormField from '../components/pageComponents/EditProfile/components/FormField';
import Header from '../components/reusable/Header';
import SelectWithPicture from '../components/reusable/SelectWithPicture';
import BackButton from '../components/reusable/buttons/BackButton';
import { BigButtons } from '../components/reusable/buttons/Buttons';

import { dispatch } from '../redux/store';

// Utils
import { notifyError, removeEmpty } from '../utils/helpers';

// Contract status options matching the design
const CONTRACT_STATUS_OPTIONS = [
    { value: 'UNSIGNED', label: 'Unsigned' },
    { value: 'CONTRACT', label: 'Contract' },
    { value: 'NON_CONTRACT', label: 'Non-Contract' }
];

// Validation schema
const contractValidationSchema = yup.object().shape({
    contractStatus: yup.string().required('Please select a contract status'),
    clubName: yup.string().when('contractStatus', {
        is: val => val === 'CONTRACT' || val === 'NON_CONTRACT',
        then: () => yup.string().required('Please select a football club'),
        otherwise: () => yup.string().notRequired()
    }),
    teamName: yup.string().when('clubName', {
        is: val => Boolean(val),
        then: () => yup.string().required('Please select a team'),
        otherwise: () => yup.string().notRequired()
    }),
    contractExpiry: yup.string().when('contractStatus', {
        is: 'CONTRACT',
        then: () => yup.string().notRequired(), // Optional field as per placeholder text
        otherwise: () => yup.string().notRequired()
    })
});

const ContractSelect = () => {
    const navigate = useNavigate();
    const userInfo = useSelector(({ auth }) => auth?.authUser?.userInfo);

    // Club and team selection state
    const [teams, setTeams] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectTeam, setSelectTeam] = useState({});
    const [selectedClub, setSelectedClub] = useState({});
    const [errorMessage, setNotFoundClubError] = useState(false);
    const [isFetchingTeams, setIsFetchingTeams] = useState(false);

    // Loading state for user update
    const loadingUserUpdate = useSelector(
        ({ loading }) => loading.effects.user.updateUser
    );

    // Formik setup
    const formik = useFormik({
        initialValues: {
            contractStatus: userInfo?.contractStatus || 'UNSIGNED',
            clubName: userInfo?.clubName || '',
            teamName: userInfo?.teamName || '',
            contractExpiry: userInfo?.contractExpiry || ''
        },
        validationSchema: contractValidationSchema,
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: values => {
            handleFormSubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue
    } = formik;

    const handleTeamChange = useCallback(
        ({ target }) => {
            const selectedTeam =
                teams.find(item =>
                    item?.teamName
                        ?.toLowerCase()
                        ?.includes(target.value?.toLowerCase())
                ) || {};

            setSelectTeam(selectedTeam);
            setFieldValue('teamName', target.value);
        },
        [teams, setFieldValue]
    );

    const handleClubSelection = useCallback(
        async clubId => {
            setIsFetchingTeams(true);
            const teamsInGivenClub = [];
            const res = await dispatch.team.getTeamsByClubId(clubId);
            teamsInGivenClub.push(...res?.data);

            if (teamsInGivenClub?.length === 1) {
                setSelectTeam(teamsInGivenClub[0]);
                setFieldValue('teamName', teamsInGivenClub[0]?.teamName);
            }
            setTeams(teamsInGivenClub);
            setIsFetchingTeams(false);
        },
        [setFieldValue]
    );

    const getTeamsBasedOnSelectedClub = useCallback(
        _debounce(handleClubSelection, 100),
        [handleClubSelection]
    );

    const handleFormSubmit = useCallback(
        async values => {
            try {
                // Prepare data for submission
                const mergedValues = {
                    ...values,
                    id: userInfo.id,
                    teamId: selectTeam?.id || '',
                    clubId: selectedClub?.id || ''
                    // clubName: selectedClub?.clubName || "",
                    // teamName: selectTeam?.teamName || ""
                };

                // Clean empty values
                const cleanData = removeEmpty(mergedValues);

                // Submit the form data
                const res = await dispatch.user.updateUser(cleanData);

                if (res === 1) {
                    // Refresh user data
                    await Promise.all([
                        dispatch.user.getUserProfile({ id: userInfo?.id }),
                        dispatch.user.userSearchByProjection()
                    ]);

                    navigate('/account-setup');
                }
            } catch (error) {
                console.error('Contract submission error:', error);
                notifyError(
                    'Failed to update contract information. Please try again.'
                );
            }
        },
        [
            dirty,
            userInfo,
            selectTeam,
            selectedClub,
            teams,
            errorMessage,
            searchValue,
            navigate
        ]
    );

    // Update club name in formik when club is selected
    useEffect(() => {
        if (selectedClub?.clubName) {
            setFieldValue('clubName', selectedClub.clubName);
            handleBlur({
                target: { name: 'clubName' }
            });
        } else if (!selectedClub?.clubName && values.clubName) {
            // Clear clubName if no club is selected
            setFieldValue('clubName', '');
        }
    }, [selectedClub, setFieldValue, values.clubName]);

    // Update team name in formik when team is selected
    useEffect(() => {
        if (selectTeam?.teamName) {
            setFieldValue('teamName', selectTeam.teamName);
            handleBlur({
                target: { name: 'teamName' }
            });
        } else if (!selectTeam?.teamName && values.teamName) {
            // Clear teamName if no team is selected
            setFieldValue('teamName', '');
        }
    }, [selectTeam, setFieldValue, values.teamName]);

    // Reset club and team when contract status changes
    useEffect(() => {
        if (values.contractStatus === 'UNSIGNED') {
            setFieldValue('clubName', '');
            setFieldValue('teamName', '');
            setFieldValue('contractExpiry', '');
            setSelectedClub({});
            setSelectTeam({});
            setTeams([]);
        } else if (values.contractStatus === 'NON_CONTRACT') {
            setFieldValue('contractExpiry', '');
        }
    }, [values.contractStatus, setFieldValue]);

    const handleBack = () => {
        navigate(-1);
    };

    // Initialize with existing user data
    useEffect(() => {
        // Initialize selected club if exists in userInfo
        if (userInfo?.clubName && userInfo?.clubId) {
            setSelectedClub({
                id: userInfo.clubId,
                clubName: userInfo.clubName
            });
        }

        // Initialize selected team if exists in userInfo
        if (userInfo?.teamName && userInfo?.teamId) {
            setSelectTeam({
                id: userInfo.teamId,
                teamName: userInfo.teamName
            });
        }
    }, [userInfo]);

    useEffect(() => {
        const loadClubs = async () => {
            await dispatch.club.getClubsByGenderAndType({
                gender: userInfo.gender,
                type: 'DOMESTIC'
            });
        };
        loadClubs();
    }, [userInfo.gender, userInfo.userType]);

    return (
        <div className='min-h-screen bg-white page'>
            {/* Header with back button */}
            <Header>
                <BackButton onClick={handleBack} />
                <div></div> {/* Empty div for spacing */}
            </Header>

            <form onSubmit={handleSubmit}>
                <div className='px-2 pb-32'>
                    {/* Page Title */}
                    <div className='mb-8'>
                        <h1 className='text-[18px] font-bold text-black uppercase  mb-4 font-favela-bold'>
                            CONTRACT STATUS
                        </h1>
                        <p className='text-[14px] text-gray-700 leading-relaxed font-poppins'>
                            What is your current contract status. Please select
                            the option that best applies to you.
                        </p>
                    </div>

                    {/* Contract Status Selection */}
                    <div className='mb-12'>
                        <RadioGroup
                            name='contractStatus'
                            value={values.contractStatus}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            options={CONTRACT_STATUS_OPTIONS}
                            direction='col'
                            className='space-y-4'
                            size='lg'
                        />
                        {errors.contractStatus && touched.contractStatus && (
                            <p className='text-red-600 text-xs mt-2'>
                                {errors.contractStatus}
                            </p>
                        )}
                    </div>

                    {/* Conditional sections based on contract status */}
                    {(values.contractStatus === 'CONTRACT' ||
                        values.contractStatus === 'NON_CONTRACT') && (
                        <>
                            {/* Football Club Section */}
                            <div className='relative w-full mb-7 group'>
                                <label
                                    htmlFor='clubs'
                                    className={`text-[14px] ${errors.clubName && touched.clubName ? 'text-red-600' : 'text-gray-500'}`}
                                >
                                    Football Club
                                </label>
                                <div className='flex items-center relative'>
                                    <div className='appearance-none border-none w-full text-gray-700 py-1 leading-tight'>
                                        <SelectWithPicture
                                            userInfo={userInfo}
                                            setSelectClub={setSelectedClub}
                                            selectClub={selectedClub}
                                            setTeams={setTeams}
                                            clubsByGender
                                            borderColor={
                                                errors.clubName &&
                                                touched.clubName
                                                    ? 'border-red-600'
                                                    : ''
                                            }
                                            setSelectTeam={setSelectTeam}
                                            setNotFoundClubError={
                                                setNotFoundClubError
                                            }
                                            searchValue={searchValue}
                                            setSearchValue={setSearchValue}
                                            getTeamsBasedOnSelectedClub={
                                                getTeamsBasedOnSelectedClub
                                            }
                                            onBlur={() =>
                                                handleBlur({
                                                    target: { name: 'clubName' }
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                {errors.clubName &&
                                    touched.clubName &&
                                    !searchValue && (
                                        <p className='text-red-600 text-xs'>
                                            {errors.clubName}
                                        </p>
                                    )}
                            </div>

                            {/* Team Section */}
                            {teams?.length ? (
                                <div className='relative w-full my-5'>
                                    <label
                                        htmlFor='teamName'
                                        className={`text-[14px] ${errors.teamName ? 'text-red-600' : 'text-gray-500'}`}
                                    >
                                        Team
                                    </label>
                                    <div className='flex items-center'>
                                        <div className='appearance-none border-none w-full text-gray-700 py-1 leading-tight'>
                                            <select
                                                name='teamName'
                                                value={values.teamName}
                                                onChange={handleTeamChange}
                                                onBlur={handleBlur}
                                                className={`appearance-none bg-transparent border-b-2 ${errors.teamName ? 'border-red-600' : 'border-gray-600'} w-full ${errors.teamName ? 'text-red-600' : 'text-gray-700'} py-1 pl-2 leading-tight focus:outline-none`}
                                            >
                                                <option value=''>
                                                    Select team
                                                </option>
                                                {teams
                                                    .sort(
                                                        (a, b) =>
                                                            b.index - a.index
                                                    )
                                                    .map((team, idx) => (
                                                        <option
                                                            key={idx}
                                                            value={
                                                                team?.teamName
                                                            }
                                                        >
                                                            {team?.teamName}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                    {errors.teamName && touched.teamName && (
                                        <p className='text-red-600 text-xs mt-1'>
                                            {errors.teamName}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                errorMessage &&
                                searchValue.length > 2 && (
                                    <div className='text-red-600 font-poppins -mt-[20px] text-xs mb-5'>
                                        <p>Team not in database.</p>
                                        <p>
                                            Please contact{' '}
                                            <a
                                                className='text-red-600'
                                                href={`mailto:hello@playerapp.co?subject=Add team to database&body=Hello PLAYER, \n Please add '${searchValue}' to your system`}
                                            >
                                                hello@playerapp.co{' '}
                                            </a>
                                            to get it added.
                                        </p>
                                    </div>
                                )
                            )}
                        </>
                    )}

                    {/* Contract Expiry Section - Only show for CONTRACT status */}
                    {values.contractStatus === 'CONTRACT' && (
                        <FormField
                            name='contractExpiry'
                            label='Contract Expiry Date'
                            value={values.contractExpiry}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder='eg. July 2025 (Leave blank if not applicable)'
                            error={errors.contractExpiry}
                            touched={touched.contractExpiry}
                        />
                    )}
                </div>

                {/* Fixed Next Button at bottom */}
                <div className='fixed bottom-0 left-0 right-0 p-4'>
                    <BigButtons
                        black
                        label={loadingUserUpdate ? 'SAVING...' : 'NEXT'}
                        type='submit'
                        isDisabled={isFetchingTeams || loadingUserUpdate}
                        isLoading={loadingUserUpdate}
                    />
                </div>
            </form>
        </div>
    );
};

export default ContractSelect;
